/** MOMAX Home Reveal V3 — one frame per actual reveal, timer-paced queue. */
(() => {
  'use strict';
  const SELECTOR = [
    '.momax-home-categories__header', '.momax-home-categories__item', '.momax-home-categories__footer',
    '.momax-scenes__header', '.momax-scenes__card',
    '.momax-featured-picks__header', '.momax-featured-picks__card', '.momax-featured-picks__footer',
    '.momax-best-sellers__header', '.mm-bs__heading', '.mm-bs__tabs', '.mm-bs__card', '.mm-bs__navigation',
    '.momax-everyday__header', '.momax-everyday__item',
    '.momax-reviews__header', '.momax-reviews__card', '.momax-reviews__product', '.momax-reviews__logo',
    '.momax-approach__content',
    '.momax-story-stats__header', '.momax-story-stats__media', '.momax-story-stats__item', '.momax-story-stats__caption',
    '.momax-newsletter__media', '.momax-newsletter__card',
    '.momax-gallery__header', '.momax-gallery__heading', '.momax-gallery__card',
    '.momax-how-it-works__header', '.momax-how-it-works__viewport',
    '.momax-journal__header', '.momax-journal__card', '.momax-journal__footer',
    '.momax-peace__header', '.momax-peace__card',
    '.momax-footer__brand', '.momax-footer__newsletter', '.momax-footer__menu',
    '.momax-footer__bottom', '.momax-footer__utilities'
  ].join(',');
  const GAP = 160;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const main = document.getElementById('main');
  if (!main || !('IntersectionObserver' in window) || !CSS.supports('translate', '0 80px')) return;

  const pending = new Set();
  const visible = new Set();
  const running = new Set();
  const seen = new WeakSet();
  let frame = 0;
  let timer = 0;
  let lastStart = -Infinity;

  function complete(element) {
    if (!(element instanceof Element)) return;
    element.dataset.momaxReveal = 'done';
    pending.delete(element);
    visible.delete(element);
    running.delete(element);
    observer.unobserve(element);
  }

  function eligibleRect(element) {
    if (!element.isConnected || element.closest('[hidden], [inert]')) return null;
    const banner = element.closest('momax-home-banner');
    if (banner && banner.dataset.state !== 'normal') return null;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 &&
      rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth
      ? rect : null;
  }

  function clearQueue() {
    if (frame) cancelAnimationFrame(frame);
    if (timer) clearTimeout(timer);
    frame = 0;
    timer = 0;
  }

  function schedule() {
    if (frame || timer || !visible.size || reduced.matches || document.hidden) return;
    const delay = Math.max(0, GAP - (performance.now() - lastStart));
    if (delay > 0) {
      // The former implementation polled every animation frame for 160ms.
      timer = setTimeout(() => { timer = 0; schedule(); }, delay);
    } else {
      frame = requestAnimationFrame(playNext);
    }
  }

  function playNext(now) {
    frame = 0;
    const ready = [];
    // One layout read per candidate, only when a reveal is actually due.
    visible.forEach(element => {
      const rect = eligibleRect(element);
      if (rect) ready.push({element, top: rect.top, left: rect.left});
    });
    if (!ready.length) return;
    ready.sort((a, b) => a.top - b.top || a.left - b.left);
    const element = ready[0].element;
    pending.delete(element);
    visible.delete(element);
    observer.unobserve(element);
    running.add(element);
    element.dataset.momaxReveal = 'running';
    lastStart = now;
    if (visible.size) schedule();
  }

  function register(root) {
    root.querySelectorAll(SELECTOR).forEach(element => {
      if (!element.closest('#main, .momax-footer') || seen.has(element) ||
          element.closest('[data-hiw-clone]')) return;
      seen.add(element);
      if (reduced.matches) { complete(element); return; }
      element.dataset.momaxReveal = 'pending';
      pending.add(element);
      observer.observe(element);
    });
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting, intersectionRect}) => {
      if (!pending.has(target)) return;
      if (isIntersecting && intersectionRect.width > 0 && intersectionRect.height > 0) {
        visible.add(target);
      } else {
        visible.delete(target);
      }
    });
    schedule();
  }, {threshold: 0.001});

  function replayFilteredCards(event) {
    const section = event.target;
    if (!(section instanceof Element) ||
        !section.matches('momax-featured-picks, momax-journal, momax-best-sellers') || !main.contains(section)) return;
    section.querySelectorAll('[data-fp-card], [data-journal-card], [data-bs-card]').forEach(card => {
      complete(card);
      if (!card.hidden && !card.closest('[hidden]')) seen.delete(card);
    });
    register(section);
  }

  document.addEventListener('momax:featured-picks:filter-change', replayFilteredCards);
  document.addEventListener('momax:journal:filter-change', replayFilteredCards);
  document.addEventListener('momax:best-sellers:tab-change', replayFilteredCards);
  document.addEventListener('animationend', event => {
    if (event.animationName === 'momax-home-reveal' &&
        event.target instanceof Element &&
        event.target.dataset.momaxReveal === 'running') complete(event.target);
  });
  document.addEventListener('focusin', event => {
    const element = event.target instanceof Element
      ? event.target.closest('[data-momax-reveal]') : null;
    if (element) complete(element);
  });
  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    clearQueue();
    [...pending, ...running].forEach(complete);
  });
  document.addEventListener('shopify:section:load', event => register(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...pending, ...running].filter(element => event.target.contains(element)).forEach(complete);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearQueue();
    else schedule();
  });

  // Inert/hidden state changes can reveal an already-intersecting component.
  // Keep this safety observer, but never start a frame without visible work.
  const changes = new MutationObserver(() => { if (visible.size) schedule(); });
  changes.observe(document.body, {
    subtree: true, attributes: true,
    attributeFilter: ['inert', 'hidden', 'data-state']
  });

  // The footer section group is a sibling of #main in theme.liquid.
  register(document);
})();
